import { IListObjectStore } from "redux/_common/type";
import { IStickyColumn, ITenant, TConfigType, TPrintType } from "./@types";
import { IVessel } from "../feedback/@types";
import { CONFIG_TABS, PRINT_TABS } from "./constants";
import toolActions from "./actions";
import { IconName } from "@blueprintjs/core";
import { EReportStatus, ITabReport } from "../logs/@types";

export const getTenantsOptionList = (tenants: IListObjectStore<ITenant>) => {
	return tenants.ids.map((tenantId) => {
		const tenantInfo = tenants.byIds[tenantId];
		return {
			label: tenantInfo?.name ?? "",
			value: tenantId,
		};
	});
};

export const getVesselsOptionList = (vessels: IListObjectStore<IVessel>) => {
	return vessels.ids.map((vesselId) => {
		const vesselInfo = vessels.byIds[vesselId];
		return {
			label: vesselInfo?.vesselName ?? "",
			value: vesselId,
		};
	});
};

export const setCurrentDropdown = (
	rowId: string,
	value: boolean,
	setRowDropdown: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
) => {
	setRowDropdown((state) => {
		const stateWithOtherRowIdsSetToFalse = Object.keys(state).reduce(
			(acc, rowId) => {
				acc[rowId] = false;
				return acc;
			},
			{} as typeof state
		);
		return {
			...stateWithOtherRowIdsSetToFalse,
			[rowId]: value,
		};
	});
};

export const isJson = (str: string) => {
	try {
		JSON.parse(str);
	} catch (e) {
		return false;
	}
	return true;
};

export const getModelName = (category: TConfigType = "logs") => {
	let modelName = "";
	if (category === "logs") {
		modelName = "logConfig";
	} else if (category === "checklists") {
		modelName = "permitConfig";
	} else if (category === "permits") {
		modelName = "permitConfig";
	}
	return modelName;
};

export const setConfigListBasedOnCategory = (
	category: CONFIG_TABS,
	configList: any,
	dispatch: any
) => {
	if (category === CONFIG_TABS.TAB_LOGS) {
		dispatch(toolActions.document.toolConfigLogsListSet(configList));
	} else if (category === CONFIG_TABS.TAB_CHECKLISTS) {
		dispatch(toolActions.document.toolConfigChecklistsListSet(configList));
	} else {
		dispatch(toolActions.document.toolConfigPermitsListSet(configList));
	}
};

export const resetPaginationOfCurrentPage = (dispatch: any) => {
	dispatch(toolActions.document.toolConfigLogPaginationCurrentPageSet(1));
	dispatch(toolActions.document.toolConfigChecklistPaginationCurrentPageSet(1));
	dispatch(toolActions.document.toolConfigPermitPaginationCurrentPageSet(1));
};

export const resetPrintPaginationOfCurrentPage = (dispatch: any) => {
	dispatch(toolActions.document.toolPrintLogPaginationCurrentPageSet(1));
	dispatch(toolActions.document.toolPrintChecklistPaginationCurrentPageSet(1));
	dispatch(toolActions.document.toolPrintPermitPaginationCurrentPageSet(1));
};

export const printListTitleExtractor = (code: string, typeOfLogs: string) => {
	let title = "";
	if (code && typeOfLogs) title = code + " | " + typeOfLogs;
	else if (!code && typeOfLogs) title = typeOfLogs;
	else if (code && !typeOfLogs) title = code;
	else title = "NA";
	return title;
};

export const setPrintListBasedOnCategory = (
	category: PRINT_TABS,
	configList: any,
	dispatch: any
) => {
	if (category === PRINT_TABS.TAB_LOGS) {
		dispatch(toolActions.document.toolPrintLogsListSet(configList));
	} else if (category === PRINT_TABS.TAB_CHECKLISTS) {
		dispatch(toolActions.document.toolPrintChecklistsListSet(configList));
	} else {
		dispatch(toolActions.document.toolPrintPermitsListSet(configList));
	}
};

export const getStatusConfig = (response: any, success: boolean | number | string, doc_status: string, isStatusNA?: boolean, isImosDeferred?: boolean) => {
	const STATUS_CONFIG: Record<EReportStatus, { label: string; icon: IconName; className: string }> = {
		SUCCESS: {
			label: "Success",
			icon: "tick-circle",
			className: "text-green-500 border-green-200 bg-green-50",
		},
		VERIFYING: {
			label: "Pending Verification",
			icon: "refresh",
			className: "text-yellow-500 border-yellow-200 bg-yellow-50",
		},
		ERROR: {
			label: "Failed",
			icon: "error",
			className: "text-red-500 border-red-200 bg-red-50",
		},
		PENDING: {
			label: "Pending Submission",
			icon: "time",
			className: "text-yellow-500 border-yellow-200 bg-yellow-50",
		},
		PROCESSING: {
			label: "Processing",
			icon: "updated",
			className: "text-blue-500 border-blue-200 bg-blue-50",
		},
		NA: {
			label: "N/A",
			icon: "document",
			className: "text-gray-500 border-gray-200 bg-gray-50",
		},
		DEFERRED: {
			label: "Deferred",
			icon: "pause",
			className: "text-gray-500 border-gray-200 bg-gray-50",
		},
	};

	
	const PENDING_DOC_STATUSES = ["APPROVED", "VERIFIED"];
	if (response && !success) return STATUS_CONFIG[EReportStatus.ERROR];
	if (response && success) return STATUS_CONFIG[EReportStatus.SUCCESS];

	if(isStatusNA) return STATUS_CONFIG[EReportStatus.NA];
	if(isImosDeferred) return STATUS_CONFIG[EReportStatus.DEFERRED];
	if (doc_status === "SUBMITTED") return STATUS_CONFIG[EReportStatus.VERIFYING];
	if (doc_status) return PENDING_DOC_STATUSES.includes(doc_status) ? STATUS_CONFIG[EReportStatus.PROCESSING] : STATUS_CONFIG[EReportStatus.PENDING];
	return STATUS_CONFIG[EReportStatus.PENDING];
};

export const getDocStatusConfig = (doc_status: "DRAFT" | "SUBMITTED" | "VERIFIED" | "APPROVED" | "RESUBMIT") => {
	const DOC_STATUS_CONFIG: Record<typeof doc_status, { label: string; icon: IconName; className: string }> = {
		DRAFT: {
			label: "Draft",
			icon: "document",
			className: "text-gray-500"
		},
		SUBMITTED: {
			label: "Submitted",
			icon: "upload",
			className: "text-yellow-500"
		},
		VERIFIED: {
			label: "Verified",
			icon: "tick-circle",
			className: "text-green-500"
		},
		APPROVED: {
			label: "Approved",
			icon: "tick-circle",
			className: "text-blue-500"
		},
		RESUBMIT: {
			label: "Resubmit",
			icon: "refresh",
			className: "text-red-500"
		},
	};

	return DOC_STATUS_CONFIG[doc_status];
};

export const getSelectedDate = (report: ITabReport) => {
	if (report?.selectedDate?.includes("T")) {
		if (report?.intlDateLineRecord) {
			return `${report?.selectedDate?.split("T")?.[0]} (IDL Repeat)`;
		}
		return `${report?.selectedDate?.split("T")?.[0]}`;
	}
	return "-";
};

export const getStickyColumnsOffsets = (columns: IStickyColumn[]) => {
	let count = 0;
	const offsets: Record<string, { sm: number; lg: number }> = {};

	// -8 as the rightmost column (Actions) is shifted a bit to cover up the space appearing on the edge
	for (const col of [...columns].reverse()) {
		offsets[col.key] = {
			sm: -8 + (col.width.sm * count),
			lg: -8 + (col.width.lg * count),
		};
		if (col.enabled) count++;
	}

	return offsets;
}